/**
 * In-app purchase orchestration. Wraps cordova-plugin-purchase v13 so
 * the rest of the SPA doesn't have to touch the global CdvPurchase
 * namespace.
 *
 * Flow:
 *
 *   1. On first call we initialise the store and register the
 *      `goastern.radar.sim.full` product as a NonConsumable on both
 *      Apple App Store and Google Play. Store catalogue + entitlement
 *      grants come from there.
 *   2. `purchaseFullAccess()` triggers the platform purchase UI.
 *   3. When the store approves a transaction we hand the receipt
 *      (Apple: `transactionId`, Google: `purchaseToken`) to
 *      `auth.goastern.com/entitlements/verify`. The server is the
 *      authoritative entitlement grant.
 *   4. After successful verification we call `onGranted()` so the SPA
 *      refreshes /auth/me and the scenario gate opens.
 *
 * Web is intentionally out of scope: the only correct way to buy the
 * sim is via Apple StoreKit or Google Play Billing. The exported
 * `isPurchaseSupported()` returns false off-device.
 */
import { Capacitor } from "@capacitor/core";

import { RADAR_SIM_FULL_SKU } from "./access.js";
import { loadToken } from "./classroom/authToken.js";

const AUTH_BASE_URL = "https://auth.goastern.com";

interface PluginTransaction {
  transactionId?: string;
  purchaseToken?: string;
  products: ReadonlyArray<{ id: string }>;
  finish(): Promise<unknown> | void;
}

interface PluginProduct {
  id: string;
  getOffer?(): { order(): Promise<unknown> } | undefined;
  offers?: ReadonlyArray<{ order(): Promise<unknown> }>;
}

interface PluginStore {
  register(
    products: ReadonlyArray<{
      id: string;
      type: string;
      platform: string;
    }>,
  ): void;
  when(): {
    approved(handler: (tx: PluginTransaction) => void | Promise<void>): unknown;
    finished?(handler: (tx: PluginTransaction) => void | Promise<void>): unknown;
  };
  initialize(platforms: ReadonlyArray<string>): Promise<unknown>;
  update(): Promise<unknown>;
  get(productId: string): PluginProduct | undefined;
}

type CdvPurchaseGlobal = {
  store: PluginStore;
  ProductType: { NON_CONSUMABLE: string };
  Platform: { APPLE_APPSTORE: string; GOOGLE_PLAY: string };
};

function readCdv(): CdvPurchaseGlobal | undefined {
  if (typeof window === "undefined") return undefined;
  const w = window as unknown as { CdvPurchase?: CdvPurchaseGlobal };
  return w.CdvPurchase;
}

function platformIdFor(cdv: CdvPurchaseGlobal): string {
  return Capacitor.getPlatform() === "ios"
    ? cdv.Platform.APPLE_APPSTORE
    : cdv.Platform.GOOGLE_PLAY;
}

/** True if this build is capable of running a purchase. The web
 *  build returns false unconditionally — purchases must happen via
 *  the App Store or Play Store so Apple/Google can take payment. */
export function isPurchaseSupported(): boolean {
  if (!Capacitor.isNativePlatform()) return false;
  return readCdv() !== undefined;
}

let initialised: Promise<void> | null = null;

async function ensureInitialised(
  onGranted: () => void,
): Promise<PluginStore> {
  const cdv = readCdv();
  if (cdv === undefined) {
    throw new Error("cordova-plugin-purchase not available on this build");
  }
  if (initialised === null) {
    initialised = (async () => {
      const platform = platformIdFor(cdv);
      cdv.store.register([
        {
          id: RADAR_SIM_FULL_SKU,
          type: cdv.ProductType.NON_CONSUMABLE,
          platform,
        },
      ]);
      cdv.store.when().approved((tx) => verifyTransaction(tx, onGranted));
      await cdv.store.initialize([platform]);
    })();
  }
  await initialised;
  return cdv.store;
}

async function verifyTransaction(
  tx: PluginTransaction,
  onGranted: () => void,
): Promise<void> {
  const product = tx.products.find((p) => p.id === RADAR_SIM_FULL_SKU);
  if (product === undefined) return;
  const platform: "ios" | "android" =
    Capacitor.getPlatform() === "ios" ? "ios" : "android";
  const receipt =
    platform === "ios" ? tx.transactionId : tx.purchaseToken;
  if (!receipt) return;
  const token = loadToken();
  if (token === null) {
    // /entitlements/verify requires a session — caller should sign in
    // before triggering the buy flow. We surface this through the UI
    // by leaving the entitlement state unchanged; the lock badge stays
    // up and the SPA prompts the user to sign in.
    return;
  }
  const res = await fetch(`${AUTH_BASE_URL}/entitlements/verify`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      platform,
      sku: RADAR_SIM_FULL_SKU,
      storeProductId: RADAR_SIM_FULL_SKU,
      receipt,
    }),
  });
  if (!res.ok) return;
  try {
    await tx.finish();
  } catch {
    /* the store will retry — non-fatal */
  }
  onGranted();
}

export type PurchaseStatus =
  | { ok: true }
  | { ok: false; reason: "not-supported" | "not-found" | "cancelled" | "error"; message?: string };

/** Trigger the platform purchase UI for the radar-sim full
 *  entitlement. Resolves once the platform has handed control back to
 *  the WebView — actual grant happens asynchronously via the store's
 *  approved event, which fires `onGranted` once /entitlements/verify
 *  returns 200. */
export async function purchaseFullAccess(
  onGranted: () => void,
): Promise<PurchaseStatus> {
  if (!isPurchaseSupported()) {
    return { ok: false, reason: "not-supported" };
  }
  try {
    const store = await ensureInitialised(onGranted);
    const product = store.get(RADAR_SIM_FULL_SKU);
    if (product === undefined) {
      return { ok: false, reason: "not-found" };
    }
    const offer =
      product.getOffer?.() ?? product.offers?.[0];
    if (offer === undefined) {
      return { ok: false, reason: "not-found" };
    }
    await offer.order();
    return { ok: true };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (/cancel/i.test(msg)) return { ok: false, reason: "cancelled" };
    return { ok: false, reason: "error", message: msg };
  }
}
